import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ASSOC } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Loader2, KeyRound } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { code });
      localStorage.setItem("aij_token", data.token);
      if (data.label) localStorage.setItem("aij_user_label", data.label);
      toast.success(`Bienvenue${data.label ? `, ${data.label}` : ""}.`);
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code d'accès incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-brand-navy text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #EA580C 0, transparent 45%), radial-gradient(circle at 80% 70%, #16A34A 0, transparent 40%)",
          }}
        />
        <div className="relative flex items-center gap-4">
          <img
            src={ASSOC.logo}
            alt="Logo AIJ"
            className="h-14 w-14 bg-white rounded-full p-1 object-contain"
          />
          <div>
            <div className="font-serif text-2xl leading-none">Action Impact</div>
            <div className="font-serif text-2xl leading-none">Jeunesse</div>
          </div>
        </div>

        <div className="relative">
          <div className="small-caps text-white/50 mb-3">Plateforme administrative</div>
          <h1 className="font-serif text-5xl leading-[1.05] mb-6 text-white">
            Gérer l'association,
            <br />
            avec <span className="text-brand-orange">clarté</span> et
            <br />
            <span className="italic text-white/90">humanité</span>.
          </h1>
          <p className="text-white/70 max-w-md leading-relaxed">
            Membres, bénévoles, salariés, comptabilité, bilan prévisionnel,
            bulletins de paie, attestations, reçus de don et cartes de membre —
            tout votre administratif dans une seule interface.
          </p>
        </div>

        <div className="relative small-caps text-white/50">
          {ASSOC.address}
          <br />
          <span className="tracking-normal normal-case text-white/60 text-xs">
            {ASSOC.email} · {ASSOC.website}
          </span>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-md bg-white border border-brand-border rounded-xl shadow-sm p-8"
        >
          <div className="flex lg:hidden items-center gap-3 mb-8 justify-center">
            <img
              src={ASSOC.logo}
              alt="Logo AIJ"
              className="h-12 w-12 object-contain"
            />
            <div className="font-serif text-xl text-brand-navy leading-tight">
              Action Impact
              <br />Jeunesse
            </div>
          </div>

          <div className="flex items-center gap-2 text-brand-orange mb-2">
            <KeyRound size={16} />
            <span className="small-caps">Accès administration</span>
          </div>
          <h2 className="font-serif text-3xl text-brand-navy mb-1">
            Saisissez votre code d'accès
          </h2>
          <p className="text-sm text-gray-500 mb-8">
            L'accès est strictement réservé aux personnes autorisées de l'association.
          </p>

          <label className="block small-caps mb-2" htmlFor="code">
            Code d'accès
          </label>
          <Input
            id="code"
            data-testid="login-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="••••••••"
            className="h-12 text-center tracking-[0.3em] text-lg"
            autoFocus
          />

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full h-12 mt-6 bg-brand-orange hover:bg-[#D04A06] text-white text-base font-medium"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Se connecter"}
          </Button>

          <p className="text-xs text-gray-400 mt-6 text-center">
            © {new Date().getFullYear()} ACTION IMPACT JEUNESSE — Association loi 1901
          </p>
        </form>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
